import { Platform } from 'react-native';
const { safeLog } = require('../utils/safeLog.js');

// Environment configuration with comprehensive validation
export interface EnvironmentConfig {
  supabaseUrl: string;
  supabaseAnonKey: string; // ANON key for client-side authentication with RLS
  // C4 (SV2-02 / SV2-18 / CL-23): the service-role key is intentionally NOT
  // declared in mobile EnvironmentConfig. Service-role operations live in
  // server/ and are reached via the API. Any code attempting to use a
  // service-role key from the mobile bundle is a security bug.
  oneSignalAppId: string;
  apiUrl: string;
}

// Enhanced key validation
const validateApiKey = (key: string, keyName: string): boolean => {
  if (!key || key.length < 10) {
    safeLog.warn(`⚠️ ${keyName} appears to be invalid or too short`);
    return false;
  }

  // Check for JWT format (ANON keys)
  if (key.startsWith('eyJ')) {
    try {
      const parts = key.split('.');
      if (parts.length < 2 || !parts[1]) {
        safeLog.error(`❌ ${keyName} JWT format invalid - not enough parts`);
        return false;
      }
      const payload = JSON.parse(atob(parts[1]));
      const expiration = new Date(payload.exp * 1000);
      const now = new Date();

      if (now >= expiration) {
        safeLog.error(`❌ ${keyName} has expired:`, expiration.toISOString());
        return false;
      }

      // SV2-19: do not log key validity / expiry — silently accept
      return true;
    } catch (error) {
      safeLog.error(`❌ ${keyName} JWT validation failed:`, error);
      return false;
    }
  }

  // Check for service role format
  if (key.startsWith('sb_secret_')) {
    return true;
  }

  if (__DEV__) {
    safeLog.warn(`⚠️ ${keyName} format not recognized, proceeding anyway`);
  }
  return true;
};

// OneSignal App ID is a PUBLIC client identifier (not a secret — it is listed
// in scripts/expo-public-allowlist.txt as publishable by SDK design). It is
// hardcoded here as the source of truth because EXPO_PUBLIC_ONESIGNAL_APP_ID
// failed to reach the JS bundle in production: getEnvVar() reads process.env
// dynamically (which Metro cannot inline) and the eas.json "${VAR}" placeholder
// was shipped unresolved. The empty/invalid App ID made OneSignal.initialize()
// register every device against a non-existent app → 0 push subscriptions.
// A correctly inlined env override still wins, but only when it is a real UUID.
const ONESIGNAL_APP_ID = '2dd33a2c-6e92-4057-8249-3bd02502b153';

// Accept an env override ONLY when it is a well-formed UUID. This rejects empty
// strings, unresolved "${EXPO_PUBLIC_ONESIGNAL_APP_ID}" placeholders, and any
// other garbage, so a misconfigured build/OTA environment can never override
// the known-good public App ID with an invalid value (the original outage).
const ONESIGNAL_APP_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function resolveOneSignalAppId(candidate: string | null | undefined): string {
  if (candidate && ONESIGNAL_APP_ID_RE.test(candidate.trim())) {
    return candidate.trim();
  }
  return ONESIGNAL_APP_ID;
}

// Helper function to safely get environment variables
function getEnvVar(key: string): string | null {
  const value = process.env[key];
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return value;
}

// Validate required environment variables with enhanced logic
const validateEnvironmentVariables = (): EnvironmentConfig => {
  // Gather all available keys. Service-role keys are intentionally absent —
  // see EnvironmentConfig comment. Mobile only reads EXPO_PUBLIC_* values.
  const availableKeys = {
    supabaseUrl: getEnvVar('EXPO_PUBLIC_SUPABASE_URL'),
    supabaseAnonKey: getEnvVar('EXPO_PUBLIC_SUPABASE_ANON_KEY'),
    oneSignalAppId: getEnvVar('EXPO_PUBLIC_ONESIGNAL_APP_ID'),
    apiUrl: getEnvVar('EXPO_PUBLIC_API_URL'),
  };

  // SV2-19: never log key previews. Only presence is logged in dev.
  if (__DEV__) {
    safeLog.info('Environment Variable Status:');
    Object.entries(availableKeys).forEach(([key, value]) => {
      safeLog.info(`- ${key}:`, value ? '✅ Present' : '❌ Missing');
    });
  }

  // Check for missing critical variables
  const criticalMissing = [];
  if (!availableKeys.supabaseUrl) criticalMissing.push('EXPO_PUBLIC_SUPABASE_URL');
  if (!availableKeys.supabaseAnonKey) criticalMissing.push('EXPO_PUBLIC_SUPABASE_ANON_KEY');

  if (criticalMissing.length > 0) {
    const errorMessage = `Missing critical environment variables: ${criticalMissing.join(', ')}`;
    safeLog.error('❌ Environment Configuration Error:', errorMessage);
    throw new Error(errorMessage);
  }

  // Validate ANON key
  if (availableKeys.supabaseAnonKey && !validateApiKey(availableKeys.supabaseAnonKey, 'ANON Key')) {
    safeLog.error('❌ ANON key validation failed');
  }

  // C4: service-role key validation removed from mobile config — see
  // EnvironmentConfig comment above. Server-side validation lives in server.js.

  // Final validation
  const config = {
    supabaseUrl: availableKeys.supabaseUrl!,
    supabaseAnonKey: availableKeys.supabaseAnonKey!,
    oneSignalAppId: resolveOneSignalAppId(availableKeys.oneSignalAppId),
    apiUrl: 'https://mobile.padrinho.app', // FORCE production API - abandon localhost URLs completely
  };

  // SV2-19: presence-only summary, dev only
  if (__DEV__) {
    safeLog.info('✅ Environment Configuration Loaded:', {
      platform: Platform.OS,
      apiUrl: config.apiUrl,
      anonKey: !!config.supabaseAnonKey,
      oneSignalAppId: !!config.oneSignalAppId,
    });
  }

  return config;
};

// Main environment configuration - validate synchronously at module load
let env: EnvironmentConfig;

try {
  env = validateEnvironmentVariables();
} catch (error) {
  // Fallback configuration for development
  safeLog.error('❌ Environment validation failed:', error);
  env = {
    supabaseUrl: 'https://kyxfbmqiamspfacaiqjg.supabase.co',
    supabaseAnonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt5eGZibXFpYW1zcGZhY2FpcWpnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI3OTg4NjgsImV4cCI6MjA2ODM3NDg2OH0.RiQGDJLpYQLvaoQVBdyOH1V2jMTIr530A8OZlIiWbwc',
    oneSignalAppId: resolveOneSignalAppId(process.env.EXPO_PUBLIC_ONESIGNAL_APP_ID),
    apiUrl: 'https://mobile.padrinho.app',
  };
}

export { env };

// Development mode helpers
export const isDevelopment = __DEV__;
export const isProduction = !__DEV__;
export const platform = Platform.OS;

// Environment-specific configurations with platform adaptations
export const config = {
  api: {
    timeout: isDevelopment ? 15000 : 8000,
    retries: isDevelopment ? 5 : 3,
    backoffMultiplier: 1.5,
  },
  auth: {
    sessionTimeout: 30 * 24 * 60 * 60 * 1000, // 30 days
    refreshThreshold: 60 * 1000, // 1 minute
    maxRetries: 3,
    retryDelay: 2000,
  },
  notifications: {
    enabled: platform !== 'web' && platform !== 'windows' && platform !== 'macos',
    batchSize: 50,
  },
  logging: {
    enabled: isDevelopment,
    level: isDevelopment ? 'debug' : 'error',
    supabaseDebug: isDevelopment,
  },
  storage: {
    preferAsyncStorage: platform === 'ios' || platform === 'android',
    preferLocalStorage: platform === 'web',
    fallbackToMemory: true,
  },
};


// Service role client should NEVER be created on client-side for security reasons
// All admin operations using service role are handled server-side only